
import React from 'react';
import { motion } from 'framer-motion';
import { Scale, Info } from 'lucide-react';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface BmiStatusCardProps {
  currentWeight: number;
  currentHeight: number;
  displayBmi: number;
  bmiStatus: string;
  bmiHealthStatus: string;
}

const BmiStatusCard: React.FC<BmiStatusCardProps> = ({
  currentWeight,
  currentHeight,
  displayBmi,
  bmiStatus,
  bmiHealthStatus
}) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <motion.div 
          className="p-4 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm pointer-events-auto"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center gap-1 mb-2">
            <Scale className="h-4 w-4 text-purple-500" />
            <span className="font-medium">Peso/IMC</span>
            <TooltipTrigger className="cursor-help">
              <Info className="h-4 w-4 text-gray-400" />
            </TooltipTrigger>
          </div>
          <div className="mt-1 flex justify-between items-center text-sm">
            <span className="font-medium text-gray-700">
              IMC: {displayBmi.toFixed(1)}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              displayBmi >= 18.5 && displayBmi <= 24.9 
                ? 'bg-green-100 text-green-800' 
                : 'bg-amber-100 text-amber-800'
            }`}>
              {bmiStatus}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <span>Peso actual: {currentWeight}kg</span>
            <span>Altura: {currentHeight}cm</span>
          </div>
        </motion.div>
        <TooltipContent className="p-2 max-w-xs">
          <p className="font-medium">IMC: {displayBmi.toFixed(1)} - {bmiStatus}</p>
          <p className="text-sm">{bmiHealthStatus}</p>
          <p className="text-xs mt-1">Rango saludable: 18.5-24.9</p>
          <ul className="text-xs mt-1">
            <li>&lt;16: Bajo peso severo</li>
            <li>16-18.4: Bajo peso</li>
            <li>18.5-24.9: Peso normal</li>
            <li>25-29.9: Sobrepeso</li>
            <li>30-34.9: Obesidad I</li>
            <li>35-39.9: Obesidad II</li>
            <li>&gt;40: Obesidad III</li>
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BmiStatusCard;
